import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { DateTime } from 'luxon'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { PlaidLink } from 'src/interfaces/oatfiBusiness'

interface Props {
  link: PlaidLink | null
  isGenerating: boolean
  onGenerate(): void
  onCopy(url: string): void
}

const UNAVAILABLE_MESSAGE =
  'Plaid did not return a hosted link for this customer, so there is no URL to send them. Try generating again, or check the Plaid configuration for this environment.'

const PlaidLinkCardComponent = ({ link, isGenerating, onGenerate, onCopy }: Props) => {
  const url = link?.url ?? null

  const handleCopy = useCallback(() => {
    if (url === null) {
      return
    }

    onCopy(url)
  }, [url, onCopy])

  return (
    <SectionCard
      title="Bank link"
      subtitle="Generate a hosted Plaid link and send it to the customer so they can connect their bank account. The lender uses that connection during underwriting."
      dataTestId="admin-customer-plaid-link-card"
    >
      {/* Read-only: the URL is generated, never typed. */}
      <input
        readOnly
        value={url ?? ''}
        placeholder="No link generated yet"
        aria-label="Hosted bank-link URL"
        data-testid="plaid-link-url"
        // Drawn like the app's inputs (36px, 8px radius, shades-300 rule) but on the grey canvas,
        // so it reads as a value to copy rather than a field to type in.
        css={tw`h-9 w-full rounded-lg border border-solid border-shades-300 bg-shades-50 px-3 font-[inherit] text-sm text-shades-500 outline-none`}
      />

      {/* A response with no hosted_link_url is a real outcome, not an error. The card says so
          rather than falling back to `linkToken` (an input to the Plaid JS SDK, not a URL a
          customer can open) or synthesising a plaid.link address. The admin-prototype copies a
          hardcoded 'https://plaid.link/…' here; that is fabricated data and must not ship. */}
      {link !== null && url === null ? (
        <Typography variant="body2" css={tw`text-warning-500`} data-testid="plaid-link-unavailable">
          {UNAVAILABLE_MESSAGE}
        </Typography>
      ) : (
        <Fragment />
      )}

      {/* The link expires, so an admin sending it tomorrow needs to know it will not work. */}
      {link?.expiration ? (
        <Typography variant="body2" css={tw`text-shades-400`}>
          Expires {DateTime.fromISO(link.expiration).toFormat('MMM d, yyyy h:mm a')}
        </Typography>
      ) : (
        <Fragment />
      )}

      <div css={tw`flex flex-row justify-end gap-2`}>
        <Button
          size="small"
          color="white"
          onClick={handleCopy}
          disabled={url === null}
          data-testid="plaid-link-copy"
        >
          Copy
        </Button>
        <Button
          size="small"
          color="dark"
          className={tw`px-4`}
          onClick={onGenerate}
          disabled={isGenerating}
          data-testid="plaid-link-generate"
        >
          Generate
        </Button>
      </div>
    </SectionCard>
  )
}

export const PlaidLinkCard = memo(PlaidLinkCardComponent)
